import React, { useEffect } from 'react';
import { Button, ButtonGroup, Modal } from '@ver-uds/react';
import { HiringSite } from 'eve-account-client';
import { FormProvider, useForm } from 'react-hook-form';
import ErrorBar from '../../ErrorBar';
import BulkUploadAlert from '../BulkUpload/BulkUploadAlert';
import AddEditHiringSiteForm from '../AddEditHiringSiteForm';

export type AddHiringSiteModalProps = {
  open: boolean;
  close(): void;
  hiringSite?: HiringSite | null;
  onSubmit: (hiringSite: HiringSite) => void;
  errorMessage: string | undefined;
  openBulkUpload: () => void;
};

const AddHiringSiteModal = ({
  open,
  close,
  hiringSite = null,
  onSubmit,
  errorMessage,
  openBulkUpload,
}: AddHiringSiteModalProps): JSX.Element => {
  const modalId = 'add-hiring-site-modal';
  const formId = `${modalId}-form`;

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { handleSubmit, reset, ...methods } = useForm<HiringSite>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      line1: hiringSite?.line1 ?? '',
      line2: hiringSite?.line2 ?? '',
      city: hiringSite?.city ?? '',
      stateCode: hiringSite?.stateCode ?? '',
      zipCode: hiringSite?.zipCode ?? '',
      countyId: hiringSite?.countyId,
    },
  });

  // Set the default form values when we've loaded them.
  useEffect(() => {
    reset({
      line1: hiringSite?.line1 ?? '',
      line2: hiringSite?.line2 ?? '',
      city: hiringSite?.city ?? '',
      stateCode: hiringSite?.stateCode ?? '',
      zipCode: hiringSite?.zipCode ?? '',
      countyId: hiringSite?.countyId,
    });
  }, [errorMessage, hiringSite, reset]);

  const onFormSubmit = handleSubmit((data) => {
    onSubmit(data);
  });

  return (
    <Modal id={modalId} header="Add Hiring Site" open={open} close={close} fixedToTop>
      <Modal.Content>
        <ErrorBar errorMessage={errorMessage} />
        <BulkUploadAlert
          openBulkUpload={() => {
            close();
            openBulkUpload();
          }}
        />
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <FormProvider handleSubmit={handleSubmit} reset={reset} {...methods}>
          <AddEditHiringSiteForm id={formId} onSubmit={onFormSubmit} />
        </FormProvider>
      </Modal.Content>
      <Modal.Footer>
        <ButtonGroup>
          <Button type="button" variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" variant="submit" form={formId} onClick={onFormSubmit}>
            Add Hiring Site
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

// istanbul ignore if: Not used for test environment; Modal.setAppElement should be called in the tests directly.
if (process.env.NODE_ENV !== 'test') {
  Modal.setAppElement('#root');
}

export default AddHiringSiteModal;
